import { useMemo, useRef } from 'react';
import { QuizQuestion } from '@shared/types';
import { useQuizPhoneOptional } from '../lib/features';
import { useT } from '../lib/i18n';

type T = (key: string) => string;

/**
 * Every question the app knows about, in one list — including the
 * `profileOnly` and `onboardingOnly` ones. Callers that render a *flow* should
 * use `getQuizQuestions` / `useQuizQuestions`; this is for label lookup and
 * for flows that pick questions by id.
 */
export function getAllQuestions(t: T): QuizQuestion[] {
  return [
    {
      id: 'location',
      category: t('quiz.categories.location'),
      question: t('quiz.questions.location.question'),
      type: 'city',
      required: true,
      interstitial: true,
      shortQuiz: true,
    },

    {
      id: 'socialBattery',
      category: t('quiz.categories.yourVibe'),
      question: t('quiz.questions.socialBattery.question'),
      type: 'single',
      required: true,
      options: [
        { value: 'always_charged', label: t('quiz.questions.socialBattery.options.always_charged'), emoji: '🔋' },
        { value: 'energized', label: t('quiz.questions.socialBattery.options.energized'), emoji: '⚡' },
        { value: 'balance', label: t('quiz.questions.socialBattery.options.balance'), emoji: '🔄' },
        { value: 'small_groups', label: t('quiz.questions.socialBattery.options.small_groups'), emoji: '🪫' },
      ],
      interstitial: true,
    },
    // Your Vibe
    {
      id: 'dinnerVibe',
      category: t('quiz.categories.yourVibe'),
      question: t('quiz.questions.dinnerVibe.question'),
      type: 'single',
      required: true,
      shortQuiz: true,
      options: [
        { value: 'life_of_table', label: t('quiz.questions.dinnerVibe.options.life_of_table'), emoji: '✨' },
        { value: 'chill_engaged', label: t('quiz.questions.dinnerVibe.options.chill_engaged'), emoji: '😌' },
        { value: 'listener', label: t('quiz.questions.dinnerVibe.options.listener'), emoji: '👂' },
      ],
    },
    {
      id: 'communicationStyle',
      category: t('quiz.categories.yourVibe'),
      question: t('quiz.questions.communicationStyle.question'),
      type: 'single',
      required: true,
      options: [
        { value: 'direct', label: t('quiz.questions.communicationStyle.options.direct'), emoji: '🎯' },
        { value: 'detailed', label: t('quiz.questions.communicationStyle.options.detailed'), emoji: '🌱' },
      ],
    },
    {
      id: 'humorStyle',
      category: t('quiz.categories.yourVibe'),
      question: t('quiz.questions.humorStyle.question'),
      type: 'single',
      required: true,
      options: [
        { value: 'playful', label: t('quiz.questions.humorStyle.options.playful'), emoji: '😊' },
        { value: 'sarcastic', label: t('quiz.questions.humorStyle.options.sarcastic'), emoji: '🐍' },
      ],
    },

    // About You
    {
      id: 'careerStage',
      category: t('quiz.categories.aboutYou'),
      question: t('quiz.questions.careerStage.question'),
      type: 'single',
      required: true,
      options: [
        { value: 'student', label: t('quiz.questions.careerStage.options.student'), emoji: '🎓' },
        { value: 'early_career', label: t('quiz.questions.careerStage.options.early_career'), emoji: '🌱' },
        { value: 'mid_career', label: t('quiz.questions.careerStage.options.mid_career'), emoji: '🚀' },
        { value: 'founder', label: t('quiz.questions.careerStage.options.founder'), emoji: '👑' },
        { value: 'freelance', label: t('quiz.questions.careerStage.options.freelance'), emoji: '🎨' },
        { value: 'transitioning', label: t('quiz.questions.careerStage.options.transitioning'), emoji: '🌀' },
        { value: 'retired', label: t('quiz.questions.careerStage.options.retired'), emoji: '🌴' },
      ],
    },
    {
      id: 'industry',
      category: t('quiz.categories.aboutYou'),
      question: t('quiz.questions.industry.question'),
      type: 'single',
      required: true,
      options: [
        { value: 'tech', label: t('quiz.questions.industry.options.tech'), emoji: '💻' },
        { value: 'arts_media', label: t('quiz.questions.industry.options.arts_media'), emoji: '🎭' },
        { value: 'nonprofit', label: t('quiz.questions.industry.options.nonprofit'), emoji: '🌍' },
        { value: 'health', label: t('quiz.questions.industry.options.health'), emoji: '🏥' },
        { value: 'finance', label: t('quiz.questions.industry.options.finance'), emoji: '💼' },
        { value: 'hospitality', label: t('quiz.questions.industry.options.hospitality'), emoji: '🍸' },
        { value: 'education', label: t('quiz.questions.industry.options.education'), emoji: '📚' },
        { value: 'other', label: t('quiz.questions.industry.options.other'), emoji: '✨' },
      ],
      interstitial: true,
    },
    {
      id: 'conversationTopics',
      category: t('quiz.categories.aboutYou'),
      question: t('quiz.questions.conversationTopics.question'),
      type: 'multiple',
      required: true,
      shortQuiz: true,
      maxSelections: 3,
      options: [
        { value: 'work_career', label: t('quiz.questions.conversationTopics.options.work_career'), emoji: '💼' },
        { value: 'pop_culture', label: t('quiz.questions.conversationTopics.options.pop_culture'), emoji: '🎭' },
        { value: 'travel', label: t('quiz.questions.conversationTopics.options.travel'), emoji: '✈️' },
        { value: 'food', label: t('quiz.questions.conversationTopics.options.food'), emoji: '🍽️' },
        { value: 'fitness', label: t('quiz.questions.conversationTopics.options.fitness'), emoji: '🏋️‍♀️' },
        { value: 'money', label: t('quiz.questions.conversationTopics.options.money'), emoji: '💰' },
        { value: 'art', label: t('quiz.questions.conversationTopics.options.art'), emoji: '🎨' },
        { value: 'tech', label: t('quiz.questions.conversationTopics.options.tech'), emoji: '📱' },
        { value: 'growth', label: t('quiz.questions.conversationTopics.options.growth'), emoji: '🌱' },
        { value: 'random', label: t('quiz.questions.conversationTopics.options.random'), emoji: '😂' },
      ],
    },
    {
      id: 'relationshipStatus',
      category: t('quiz.categories.aboutYou'),
      question: t('quiz.questions.relationshipStatus.question'),
      type: 'single',
      required: true,
      options: [
        { value: 'single', label: t('quiz.questions.relationshipStatus.options.single'), emoji: '💫' },
        { value: 'dating', label: t('quiz.questions.relationshipStatus.options.dating'), emoji: '💕' },
        { value: 'relationship', label: t('quiz.questions.relationshipStatus.options.relationship'), emoji: '❤️' },
        { value: 'married', label: t('quiz.questions.relationshipStatus.options.married'), emoji: '💍' },
        { value: 'widowed', label: t('quiz.questions.relationshipStatus.options.widowed'), emoji: '🖤' },
        { value: 'complicated', label: t('quiz.questions.relationshipStatus.options.complicated'), emoji: '🤷' },
        { value: 'prefer_not_say', label: t('quiz.questions.relationshipStatus.options.prefer_not_say'), emoji: '🙊' },
      ],
    },
    {
      id: 'genderIdentity',
      category: t('quiz.categories.aboutYou'),
      question: t('quiz.questions.genderIdentity.question'),
      type: 'multiple',
      required: true,
      options: [
        { value: 'cis_woman', label: t('quiz.questions.genderIdentity.options.cis_woman') },
        { value: 'cis_man', label: t('quiz.questions.genderIdentity.options.cis_man') },
        { value: 'non_binary', label: t('quiz.questions.genderIdentity.options.non_binary') },
        { value: 'trans_man', label: t('quiz.questions.genderIdentity.options.trans_man') },
        { value: 'trans_woman', label: t('quiz.questions.genderIdentity.options.trans_woman') },
        { value: 'genderqueer', label: t('quiz.questions.genderIdentity.options.genderqueer') },
        { value: 'prefer_not_say', label: t('quiz.questions.genderIdentity.options.prefer_not_say') },
        { value: 'other', label: t('quiz.questions.genderIdentity.options.other') },
      ],
      shortQuiz: true,
    },
    {
      id: 'pronouns',
      category: t('quiz.categories.aboutYou'),
      question: t('quiz.questions.pronouns.question'),
      type: 'multiple',
      required: true,
      options: [
        { value: 'she_her', label: t('quiz.questions.pronouns.options.she_her') },
        { value: 'he_him', label: t('quiz.questions.pronouns.options.he_him') },
        { value: 'they_them', label: t('quiz.questions.pronouns.options.they_them') },
        { value: 'she_they', label: t('quiz.questions.pronouns.options.she_they') },
        { value: 'he_they', label: t('quiz.questions.pronouns.options.he_they') },
        { value: 'other', label: t('quiz.questions.pronouns.options.other') },
        { value: 'prefer_not_say', label: t('quiz.questions.pronouns.options.prefer_not_say') },
      ],
    },
    {
      id: 'sexualOrientation',
      category: t('quiz.categories.aboutYou'),
      question: t('quiz.questions.sexualOrientation.question'),
      type: 'multiple',
      required: true,
      options: [
        { value: 'gay', label: t('quiz.questions.sexualOrientation.options.gay'), emoji: '🌈' },
        { value: 'lesbian', label: t('quiz.questions.sexualOrientation.options.lesbian'), emoji: '🌈' },
        { value: 'bisexual', label: t('quiz.questions.sexualOrientation.options.bisexual'), emoji: '💖' },
        { value: 'pansexual', label: t('quiz.questions.sexualOrientation.options.pansexual'), emoji: '🌊' },
        { value: 'queer', label: t('quiz.questions.sexualOrientation.options.queer'), emoji: '🌟' },
        { value: 'asexual', label: t('quiz.questions.sexualOrientation.options.asexual'), emoji: '🌱' },
        { value: 'prefer_not_say', label: t('quiz.questions.sexualOrientation.options.prefer_not_say'), emoji: '🙊' },
        { value: 'other', label: t('quiz.questions.sexualOrientation.options.other') },
      ],
      shortQuiz: true,
    },
    {
      id: 'birthday',
      category: t('quiz.categories.aboutYou'),
      question: t('quiz.questions.birthday.question'),
      type: 'date',
      required: true,
      placeholder: t('quiz.questions.birthday.placeholder'),
      interstitial: true,
      shortQuiz: true,
    },

    // Values
    {
      id: 'communityPreference',
      category: t('quiz.categories.yourValues'),
      question: t('quiz.questions.communityPreference.question'),
      type: 'single',
      required: true,
      options: [
        { value: 'similar', label: t('quiz.questions.communityPreference.options.similar'), emoji: '🌈' },
        { value: 'diverse', label: t('quiz.questions.communityPreference.options.diverse'), emoji: '🌍' },
      ],
    },
    {
      id: 'politicalViews',
      category: t('quiz.categories.yourValues'),
      question: t('quiz.questions.politicalViews.question'),
      type: 'scale',
      required: true,
      scaleMin: 1,
      scaleMax: 7,
      scaleLabels: {
        min: t('quiz.questions.politicalViews.scaleLabels.min'),
        max: t('quiz.questions.politicalViews.scaleLabels.max'),
      },
    },

    // Connect
    {
      id: 'phone',
      category: t('quiz.categories.connect'),
      question: t('quiz.questions.phone.question'),
      type: 'phone',
      required: true,
      placeholder: t('quiz.questions.phone.placeholder'),
      shortQuiz: true,
    },
    {
      id: 'instagram',
      category: t('quiz.categories.connect'),
      question: t('quiz.questions.instagram.question'),
      type: 'text',
      required: false,
      shortQuiz: true,
      placeholder: t('quiz.questions.instagram.placeholder'),
    },

    // ─── Onboarding-flow-only questions ─────────────────────────────────────
    // Act 1 of the v3 onboarding flow (components/onboarding) opens with the
    // user, not the product: what they're looking for, and how they meet
    // people today. `onboardingOnly` keeps them out of the legacy signup quiz
    // and the Connection Profile so the flow experiment owns them entirely.

    {
      id: 'connectionIntent',
      category: t('quiz.categories.yourVibe'),
      question: t('quiz.questions.connectionIntent.question'),
      type: 'multiple',
      required: true,
      onboardingOnly: true,
      options: [
        { value: 'expand_circle', label: t('quiz.questions.connectionIntent.options.expand_circle') },
        { value: 'dinner_people', label: t('quiz.questions.connectionIntent.options.dinner_people') },
        { value: 'new_in_city', label: t('quiz.questions.connectionIntent.options.new_in_city') },
        { value: 'travel_often', label: t('quiz.questions.connectionIntent.options.travel_often') },
        { value: 'shy', label: t('quiz.questions.connectionIntent.options.shy') },
        { value: 'regular_plans', label: t('quiz.questions.connectionIntent.options.regular_plans') },
        { value: 'not_met_them_yet', label: t('quiz.questions.connectionIntent.options.not_met_them_yet') },
        { value: 'apps_not_my_scene', label: t('quiz.questions.connectionIntent.options.apps_not_my_scene') },
      ],
    },
    {
      id: 'meetingChannels',
      category: t('quiz.categories.yourVibe'),
      question: t('quiz.questions.meetingChannels.question'),
      type: 'multiple',
      required: true,
      onboardingOnly: true,
      options: [
        { value: 'apps', label: t('quiz.questions.meetingChannels.options.apps') },
        { value: 'nightlife', label: t('quiz.questions.meetingChannels.options.nightlife') },
        { value: 'friends', label: t('quiz.questions.meetingChannels.options.friends') },
        { value: 'events', label: t('quiz.questions.meetingChannels.options.events') },
        { value: 'not_really', label: t('quiz.questions.meetingChannels.options.not_really') },
      ],
    },

    // ─── Connection-Profile-only questions ──────────────────────────────────
    // Richer matching/kiki signal. `profileOnly` keeps them out of signup; they
    // surface in the Connection Profile. The first four reuse existing (already
    // translated) keys; the rest are new (English now, i18n can follow).

    {
      id: 'groupRole',
      category: t('quiz.categories.yourVibe'),
      question: t('quiz.questions.groupRole.question'),
      type: 'single',
      required: false,
      profileOnly: true,
      options: [
        { value: 'steering', label: t('quiz.questions.groupRole.options.steering'), emoji: '🎙️' },
        { value: 'supporting', label: t('quiz.questions.groupRole.options.supporting'), emoji: '👂' },
      ],
    },
    {
      id: 'hobbies',
      category: t('quiz.categories.aboutYou'),
      question: t('quiz.questions.hobbies.question'),
      type: 'multiple',
      required: false,
      profileOnly: true,
      maxSelections: 3,
      options: [
        { value: 'creative', label: t('quiz.questions.hobbies.options.creative'), emoji: '🎨' },
        { value: 'fitness', label: t('quiz.questions.hobbies.options.fitness'), emoji: '🏃‍♀️' },
        { value: 'cooking', label: t('quiz.questions.hobbies.options.cooking'), emoji: '🍳' },
        { value: 'gaming', label: t('quiz.questions.hobbies.options.gaming'), emoji: '🎮' },
        { value: 'reading', label: t('quiz.questions.hobbies.options.reading'), emoji: '📚' },
        { value: 'activism', label: t('quiz.questions.hobbies.options.activism'), emoji: '✊' },
        { value: 'fashion', label: t('quiz.questions.hobbies.options.fashion'), emoji: '👗' },
        { value: 'events', label: t('quiz.questions.hobbies.options.events'), emoji: '🎭' },
        { value: 'travel', label: t('quiz.questions.hobbies.options.travel'), emoji: '✈️' },
        { value: 'tech', label: t('quiz.questions.hobbies.options.tech'), emoji: '💻' },
      ],
    },
    {
      id: 'conflictStyle',
      category: t('quiz.categories.yourValues'),
      question: t('quiz.questions.conflictStyle.question'),
      type: 'single',
      required: false,
      profileOnly: true,
      options: [
        { value: 'avoid', label: t('quiz.questions.conflictStyle.options.avoid'), emoji: '🙅' },
        { value: 'respectful', label: t('quiz.questions.conflictStyle.options.respectful'), emoji: '🤝' },
        { value: 'debate', label: t('quiz.questions.conflictStyle.options.debate'), emoji: '🔥' },
      ],
    },
    {
      id: 'topicsOpenTo',
      category: t('quiz.categories.yourValues'),
      question: t('quiz.questions.topicsOpenTo.question'),
      type: 'multiple',
      required: false,
      profileOnly: true,
      maxSelections: 3,
      options: [
        { value: 'lgbtq', label: t('quiz.questions.topicsOpenTo.options.lgbtq'), emoji: '🌈' },
        { value: 'climate', label: t('quiz.questions.topicsOpenTo.options.climate'), emoji: '🌍' },
        { value: 'equity', label: t('quiz.questions.topicsOpenTo.options.equity'), emoji: '✊' },
        { value: 'immigration', label: t('quiz.questions.topicsOpenTo.options.immigration'), emoji: '✈️' },
        { value: 'economy', label: t('quiz.questions.topicsOpenTo.options.economy'), emoji: '💸' },
        { value: 'healthcare', label: t('quiz.questions.topicsOpenTo.options.healthcare'), emoji: '🏥' },
        { value: 'skip', label: t('quiz.questions.topicsOpenTo.options.skip'), emoji: '🙊' },
      ],
    },
    {
      id: 'dietaryNeeds',
      category: 'Dining & comfort',
      question: 'Any dietary needs? (so we seat you where you can actually eat)',
      type: 'multiple',
      required: false,
      profileOnly: true,
      options: [
        { value: 'none', label: 'No restrictions', emoji: '🍽️' },
        { value: 'vegetarian', label: 'Vegetarian', emoji: '🥗' },
        { value: 'vegan', label: 'Vegan', emoji: '🌱' },
        { value: 'pescatarian', label: 'Pescatarian', emoji: '🐟' },
        { value: 'gluten_free', label: 'Gluten-free', emoji: '🌾' },
        { value: 'dairy_free', label: 'Dairy-free', emoji: '🥛' },
        { value: 'nut_allergy', label: 'Nut allergy', emoji: '🥜' },
        { value: 'halal', label: 'Halal', emoji: '🕌' },
        { value: 'kosher', label: 'Kosher', emoji: '✡️' },
        { value: 'other', label: 'Other', emoji: '✨' },
      ],
    },
    {
      id: 'drinking',
      category: 'Dining & comfort',
      question: "What's your relationship with drinking?",
      type: 'single',
      required: false,
      profileOnly: true,
      options: [
        { value: 'love_drink', label: 'Always down for a drink', emoji: '🍸' },
        { value: 'social', label: 'Social drinker', emoji: '🥂' },
        { value: 'sober_curious', label: 'Sober-curious / cutting back', emoji: '🌿' },
        { value: 'sober', label: 'Sober — I prefer alcohol-free', emoji: '🚫' },
      ],
    },
    {
      id: 'cityTenure',
      category: t('quiz.categories.aboutYou'),
      question: 'How long have you been in your city?',
      type: 'single',
      required: false,
      profileOnly: true,
      options: [
        { value: 'born_raised', label: 'Born & raised here', emoji: '🏡' },
        { value: 'established', label: 'Here for years', emoji: '📍' },
        { value: 'recently_moved', label: 'Just moved here', emoji: '🧳' },
        { value: 'visiting', label: 'Just visiting', emoji: '🗺️' },
      ],
    },
    {
      id: 'buddyActivities',
      category: 'Kikis',
      question: 'What would you love a buddy for? (helps us suggest kikis)',
      type: 'multiple',
      required: false,
      profileOnly: true,
      options: [
        { value: 'restaurants', label: 'Trying new restaurants', emoji: '🍜' },
        { value: 'workouts', label: 'Workouts & run clubs', emoji: '🏃' },
        { value: 'nightlife', label: 'Going out / nightlife', emoji: '🪩' },
        { value: 'arts', label: 'Museums, galleries & shows', emoji: '🎭' },
        { value: 'outdoors', label: 'Hikes & the outdoors', emoji: '🌲' },
        { value: 'travel', label: 'Travel & day trips', emoji: '✈️' },
        { value: 'games', label: 'Game nights', emoji: '🎲' },
        { value: 'coffee', label: 'Coffee & co-working', emoji: '☕' },
        { value: 'creative', label: 'Creative projects', emoji: '🎨' },
      ],
    },
  ];
}

/**
 * Returns quiz questions with translations applied.
 * Prefer useQuizQuestions() in components — it handles memoization and the short-quiz flag.
 */
export function getQuizQuestions(t: T, shortQuiz = false): QuizQuestion[] {
  // `profileOnly` questions are edited in the Connection Profile but never shown
  // in the onboarding signup flow (keeps signup lean). `onboardingOnly` is the
  // mirror image: asked by the v3 onboarding flow, never here.
  const all = getAllQuestions(t).filter((q) => !q.profileOnly && !q.onboardingOnly);
  return shortQuiz ? all.filter((q) => q.shortQuiz) : all;
}

/**
 * Hook that returns the memoized quiz questions for the current user.
 *
 * Everyone gets the full set. The short variant (`quiz_var=short|v2short`)
 * was measured and lost — it cost completion, checkout and purchase against
 * the full v2 — so the branch is gone rather than left as a flag nobody
 * should turn on. `getQuizQuestions` keeps its `short` parameter for the
 * admin previews that still ask for it.
 */
export function useQuizQuestions() {
  const { t, language } = useT();
  const tRef = useRef(t);
  tRef.current = t;
  const phoneOptional = useQuizPhoneOptional();
  return useMemo(() => {
    const questions = getQuizQuestions(tRef.current, false);
    if (!phoneOptional) return questions;
    return questions.map((q) =>
      q.id === 'phone' ? { ...q, required: false } : q,
    );
    // Keyed on the language, not on `t`. See `useAllQuizQuestions`. The rule
    // can't see that `tRef.current` tracks the language, so it reads the
    // dependency as unnecessary; dropping it would freeze the copy on the
    // language the component first rendered in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, phoneOptional]);
}

/**
 * Every question, translated, memoised on the *language* rather than on `t`.
 *
 * react-i18next hands back a new `t` on renders where nothing about the
 * translations has changed, so a memo keyed on it misses constantly — and this
 * one rebuilds twenty-seven questions, each running its text and every option
 * label through a lookup. On a screen you tap repeatedly without leaving (the
 * multi-selects), that ran on every tap and was most of the delay between the
 * finger and the checkmark on an older phone.
 *
 * `t` is read through a ref so the content still follows a language change:
 * the language is the dependency, and the ref supplies the current translator
 * when it fires.
 */
export function useAllQuizQuestions() {
  const { t, language } = useT();
  const tRef = useRef(t);
  tRef.current = t;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => getAllQuestions(tRef.current), [language]);
}
